/**
 * ChangePack.jsx: BM-H3980 (8 Oct 2026).
 *
 * Policy changes from the horizon scan, gathered into one change pack.
 * Vanessa, 8 Oct 2026: each policy's owner drafts and approves its new text;
 * the MLRO may also approve the AML policy; the pack publishes every new
 * version on one date. Nothing is live until the pack is published.
 * Server: /api/policy-change-packs (backend/hub/policy_change_pack.py).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FileText, ExternalLink, CheckCircle2, Send } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import PolicyEditor from "./PolicyEditor";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

const BASE = `${API}/policy-change-packs`;
const INPUT =
  "px-2 py-1 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200";
const STATE = {
  draft: ["Draft", "bg-amber-900/50 text-amber-200"],
  approved: ["Approved", "bg-emerald-900/50 text-emerald-200"],
  withdrawn: ["Withdrawn", "bg-slate-800 text-slate-400"],
  published: ["Published", "bg-sky-900/50 text-sky-200"],
};
const PACK_STATE = { open: "Open", published: "Published", partly_published: "Partly published: publish again to finish" };
// UK time always. Older records came back with no zone; they are UTC.
const when = (iso) => {
  if (!iso) return "";
  const utc = /([zZ]|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`;
  return new Date(utc).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/London" });
};
const owner = (r) => [(r.owner_role || "").toUpperCase(), r.owner_person].filter(Boolean).join(" ");

function Request({ r, onEdit, onApprove, onWithdraw }) {
  const [label, cls] = STATE[r.status] || [r.status, "bg-slate-800 text-slate-300"];
  const [withdrawing, setWithdrawing] = useState(false);
  const [why, setWhy] = useState("");
  const live = r.status === "draft" || r.status === "approved";
  return (
    <article className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2" data-testid={`cp-request-${r.policy_slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-100">{[r.policy_ref, r.policy_name].filter(Boolean).join(" ")}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{label}</span>
        <span className="text-xs text-slate-400">Owner: {owner(r) || "not recorded"}</span>
      </div>
      <div className="text-xs text-slate-300">
        Now {r.current_version || "no version"}
        {r.proposed ? ` · draft saved by ${r.proposed.saved_by} ${when(r.proposed.saved_at)} · becomes ${r.proposed.version_label || "the next point release"}` : " · nothing drafted yet"}
        {r.approved_by ? ` · approved by ${r.approved_by}` : ""}
        {r.published_version_label ? ` · published as ${r.published_version_label}` : ""}
      </div>
      {r.proposed?.reason ? <div className="text-xs text-slate-300">Reason: {r.proposed.reason}</div> : null}
      <ul className="text-xs text-slate-300 space-y-0.5">
        {(r.scan_items || []).map((s) => (
          <li key={s.id}>
            <a href={s.url} target="_blank" rel="noreferrer" className="text-sky-300 underline inline-flex items-center gap-1">
              {s.title} <ExternalLink size={10} />
            </a>
            {s.source_name ? <span className="text-slate-500"> · {s.source_name}</span> : null}
          </li>
        ))}
      </ul>
      {live && r.may_own ? (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => onEdit(r)} data-testid={`cp-edit-${r.policy_slug}`}
            className="px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200 hover:border-sky-400">
            {r.proposed ? "Edit the draft" : "Edit policy"}
          </button>
          {r.status === "draft" && r.proposed ? (
            <button type="button" onClick={() => onApprove(r)} data-testid={`cp-approve-${r.policy_slug}`}
              className="px-2 py-1 rounded-md bg-emerald-800 hover:bg-emerald-700 text-xs text-white inline-flex items-center gap-1">
              <CheckCircle2 size={12} /> Approve
            </button>
          ) : null}
          {withdrawing ? (
            <>
              <input className={`${INPUT} flex-1 min-w-[12rem]`} placeholder="Why it no longer needs changing"
                value={why} onChange={(e) => setWhy(e.target.value)} data-testid={`cp-withdraw-reason-${r.policy_slug}`} />
              <button type="button" disabled={!why.trim()} onClick={() => onWithdraw(r, why)}
                data-testid={`cp-withdraw-confirm-${r.policy_slug}`}
                className="px-2 py-1 rounded-md border border-red-700 text-xs text-red-200 disabled:opacity-50">
                Withdraw from the pack
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setWithdrawing(true)} data-testid={`cp-withdraw-${r.policy_slug}`}
              className="px-2 py-1 rounded-md border border-slate-700 text-xs text-slate-400">
              Withdraw
            </button>
          )}
        </div>
      ) : null}
      {live && !r.may_own ? <div className="text-xs text-slate-500">Only the policy's owner can edit or approve it.</div> : null}
    </article>
  );
}

export default function ChangePack() {
  const [pack, setPack] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, h] = await Promise.all([
        axios.get(`${BASE}/open`, { headers: authHeaders() }),
        axios.get(BASE, { headers: authHeaders() }),
      ]);
      setPack(o.data.pack || null);
      setHistory((h.data.packs || []).filter((p) => p.status !== "open"));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the change pack"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    try {
      await axios.post(`${BASE}/requests/${r._id}/approve`, {}, { headers: authHeaders() });
      await load();
    } catch (err) {
      setError(errorMessage(err, "Could not approve"));
    }
  };

  const withdraw = async (r, reason) => {
    try {
      await axios.post(`${BASE}/requests/${r._id}/withdraw`, { reason }, { headers: authHeaders() });
      await load();
    } catch (err) {
      setError(errorMessage(err, "Could not withdraw"));
    }
  };

  const publish = async () => {
    setPublishing(true);
    setError("");
    try {
      const r = await axios.post(`${BASE}/${pack._id}/publish`, {}, { headers: authHeaders() });
      const lines = (r.data.published || []).map((p) =>
        `${p.policy_slug} ${p.version_label} (${p.links_moved} link(s) carried across; ${p.links_flagged} to re-verify; ${p.attestations_invalidated} sign-off(s) to renew${p.archive?.archived ? "" : `; not archived yet: ${p.archive?.reason || "unknown"}`})`);
      setNotice(`Published on ${when(r.data.effective_from)}: ${lines.join("; ")}.`);
      setConfirming(false);
      await load();
    } catch (err) {
      setError(errorMessage(err, "The change pack could not be published"));
    } finally {
      setPublishing(false);
    }
  };

  // Packs published before process changes existed (24 Oct 2026) open theirs here.
  const openProcessChanges = async (p) => {
    setError("");
    try {
      const r = await axios.post(`${API}/policy-process-changes/from-pack/${p._id}`, {}, { headers: authHeaders() });
      const opened = r.data.opened || [];
      setNotice(opened.length
        ? `Opened ${opened.join(", ")}. See Process changes.`
        : "Every policy in that pack already has its process change open.");
    } catch (err) {
      setError(errorMessage(err, "Could not open the process changes"));
    }
  };

  if (pack === undefined && !error) return <div className="p-6 text-sm text-slate-400">Loading the change pack…</div>;
  const requests = pack?.requests || [];

  return (
    <div className="p-6 max-w-5xl space-y-5" data-testid="cp-page">
      <header className="flex flex-wrap items-center gap-3">
        <FileText size={20} className="text-sky-300" />
        <h1 className="text-xl font-semibold text-slate-100">Change pack</h1>
      </header>
      <p className="text-sm text-slate-400 max-w-3xl">
        Policy changes from the horizon scan, gathered together. Each policy's owner drafts its new text and approves it.
        Nothing is live until the pack is published: then every new version takes effect on the same date, its links
        move across (changed sections flagged to re-verify), staff sign-offs for those policies restart, and each policy
        opens a <a href="/compliance/process-changes" className="text-sky-300 underline">process change</a> for what CaseMinder must now do differently.
      </p>
      {error ? <div className="text-sm text-red-300" data-testid="cp-error">{error}</div> : null}
      {notice ? <div className="text-sm text-emerald-300" data-testid="cp-notice">{notice}</div> : null}

      {!pack ? (
        <div className="text-sm text-slate-400" data-testid="cp-none">
          No change pack is open. Action an item on the <a href="/compliance/horizon-scan" className="text-sky-300 underline">horizon scan</a> to start one.
        </div>
      ) : (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-100">{pack.title}</h2>
            <span className="text-xs text-slate-400">{PACK_STATE[pack.status] || pack.status}</span>
          </div>
          {requests.map((r) => (
            <Request key={r._id} r={r} onEdit={setEditing} onApprove={approve} onWithdraw={withdraw} />
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {!pack.may_publish ? (
              <span className="text-xs text-slate-500">Publishing is for the MLCO or COLP.</span>
            ) : !pack.ready_to_publish ? (
              <span className="text-xs text-slate-400" data-testid="cp-not-ready">Every policy in the pack must be approved (or withdrawn) before it can be published.</span>
            ) : confirming ? (
              <>
                <span className="text-xs text-amber-200">Every approved policy becomes live now, on one date. Staff sign-offs for them restart.</span>
                <button type="button" onClick={publish} disabled={publishing} data-testid="cp-publish-confirm"
                  className="px-3 py-1.5 rounded-md bg-sky-700 hover:bg-sky-600 text-sm text-white disabled:opacity-60">
                  {publishing ? "Publishing…" : "Yes, publish"}
                </button>
                <button type="button" onClick={() => setConfirming(false)} className="px-2 py-1 text-xs text-slate-400">Cancel</button>
              </>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} data-testid="cp-publish"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-sky-700 hover:bg-sky-600 text-sm text-white">
                <Send size={14} /> Publish the pack
              </button>
            )}
          </div>
        </section>
      )}

      {history.length ? (
        <section className="space-y-1">
          <h2 className="text-sm font-semibold text-slate-100">Earlier packs</h2>
          <ul className="text-xs text-slate-300 space-y-1" data-testid="cp-history">
            {history.map((p) => (
              <li key={p._id}>
                {p.title} · {PACK_STATE[p.status] || p.status} {when(p.published_at)}
                {(p.published || []).length ? `: ${(p.published || []).map((x) => `${x.policy_slug} ${x.version_label}`).join(", ")}` : ""}
                {p.publish_error ? <span className="text-red-300"> · {p.publish_error.policy_slug}: {p.publish_error.error}</span> : null}
                {(p.published || []).length ? (
                  <button type="button" onClick={() => openProcessChanges(p)} data-testid={`cp-open-process-${p._id}`}
                    title="Open a process change for each policy this pack published, if one is not open already"
                    className="ml-2 text-sky-300 underline">Open process changes</button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {editing ? (
        <PolicyEditor changeRequest={editing} onSaved={load} onClose={() => { setEditing(null); load(); }} />
      ) : null}
    </div>
  );
}
